import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/lib/auth'
import { Zap, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react'

export default function SignIn() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return
    setLoading(true)
    setError('')
    try {
      const { error } = await signIn(email, password)
      if (error) {
        setError('Invalid email or password')
      } else {
        navigate('/app')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      {/* Background effects */}
      <div style={{ position: 'absolute', top: '20%', left: '30%', width: 600, height: 600, background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '20%', width: 400, height: 400, background: 'radial-gradient(circle, color-mix(in srgb, var(--positive) var(--status-tint-faint), transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ width: '100%', maxWidth: 400, position: 'relative' }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: 'var(--logo-glow)' }}>
            <Zap style={{ width: 24, height: 24, color: 'var(--accent-ink)' }} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 6px' }}>
            Welcome back
          </h1>
          <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>
            Sign in to your workspace
          </p>
        </div>

        {/* Form */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 20, padding: '32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ background: 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-strong), transparent)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--danger)' }}
              >
                {error}
              </motion.div>
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 7, letterSpacing: '0.02em' }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  style={{ width: '100%', padding: '11px 14px 11px 38px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 10, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 150ms' }}
                  onFocus={e => e.target.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 7, letterSpacing: '0.02em' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  style={{ width: '100%', padding: '11px 40px 11px 38px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 10, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 150ms' }}
                  onFocus={e => e.target.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex' }}>
                  {showPassword ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading || !email || !password}
              className="btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14, marginTop: 4 }}>
              {loading ? 'Signing in...' : (
                <>Sign in <ArrowRight style={{ width: 15, height: 15 }} /></>
              )}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--ink-tertiary)' }}>
          Don't have an account?{' '}
          <Link to="/signup" style={{ color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 }}>
            Sign up free
          </Link>
        </p>
      </motion.div>
    </div>
  )
}
